import {
  ArrowTrendingUpIcon,
  CalculatorIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline';
import React, { useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

interface PricingData {
  vehicle_id: string;
  year: number;
  make: string;
  model: string;
  asking_price: number;
  wholesale_value: number;
  retail_value: number;
  purchase_price: number;
  profit_margin: number;
  days_on_lot: number;
  status: string;
}

interface PricingTrendChartsProps {
  pricingData: PricingData[];
  loading?: boolean;
}

interface ChartData {
  priceDistribution: Array<{
    range: string;
    min: number;
    max: number;
    count: number;
  }>;
  profitByMake: Array<{
    make: string;
    totalVehicles: number;
    avgProfit: number;
    totalProfit: number;
    avgPrice: number;
  }>;
  correlationData: Array<{
    price: number;
    profit: number;
    days: number;
    make: string;
  }>;
}

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'];

const PricingTrendCharts: React.FC<PricingTrendChartsProps> = ({
  pricingData,
  loading = false,
}) => {
  const [selectedChart, setSelectedChart] = useState<
    'trends' | 'distribution' | 'profit' | 'correlation'
  >('trends');

  // Process data for different chart types
  const chartData: ChartData = useMemo(() => {
    if (!pricingData || pricingData.length === 0) {
      return {
        priceDistribution: [],
        profitByMake: [],
        correlationData: [],
      };
    }

    // Group by price ranges for distribution
    const priceRanges = [
      { range: '$0-5K', min: 0, max: 5000, count: 0 },
      { range: '$5K-10K', min: 5000, max: 10000, count: 0 },
      { range: '$10K-15K', min: 10000, max: 15000, count: 0 },
      { range: '$15K-20K', min: 15000, max: 20000, count: 0 },
      { range: '$20K-30K', min: 20000, max: 30000, count: 0 },
      { range: '$30K+', min: 30000, max: Infinity, count: 0 },
    ];

    // Group by make for profit analysis
    const makeData: { [key: string]: any } = {};

    pricingData.forEach((vehicle) => {
      // Price distribution
      const price = vehicle.asking_price || 0;
      const range = priceRanges.find((r) => price >= r.min && price < r.max);
      if (range) range.count++;

      // Profit by make
      const make = vehicle.make;
      if (!makeData[make]) {
        makeData[make] = {
          make,
          totalVehicles: 0,
          avgProfit: 0,
          totalProfit: 0,
          avgPrice: 0,
        };
      }

      makeData[make].totalVehicles++;
      const profit = (vehicle.asking_price || 0) - (vehicle.purchase_price || 0);
      makeData[make].totalProfit += profit;
      makeData[make].avgPrice += vehicle.asking_price || 0;
    });

    // Calculate averages
    Object.values(makeData).forEach((data: any) => {
      data.avgProfit = data.totalProfit / data.totalVehicles;
      data.avgPrice = data.avgPrice / data.totalVehicles;
    });

    return {
      priceDistribution: priceRanges,
      profitByMake: Object.values(makeData),
      correlationData: pricingData.map((v) => ({
        price: v.asking_price || 0,
        profit: (v.asking_price || 0) - (v.purchase_price || 0),
        days: v.days_on_lot || 0,
        make: v.make,
      })),
    };
  }, [pricingData]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (!pricingData || pricingData.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-center py-12">
          <ChartBarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Pricing Data Available</h3>
          <p className="text-gray-500">Unable to generate pricing trend charts.</p>
        </div>
      </div>
    );
  }

  const renderChart = () => {
    switch (selectedChart) {
      case 'trends':
        return (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Price Distribution</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData.priceDistribution}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="range" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#3B82F6" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Average Prices by Make</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData.profitByMake}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="make" />
                  <YAxis />
                  <Tooltip
                    formatter={(value) => [`$${value?.toLocaleString()}`, 'Average Price']}
                  />
                  <Bar dataKey="avgPrice" fill="#10B981" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        );

      case 'distribution':
        return (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Price vs Profit Correlation</h3>
            <ResponsiveContainer width="100%" height={400}>
              <ScatterChart data={chartData.correlationData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  type="number"
                  dataKey="price"
                  name="Asking Price"
                  domain={['dataMin', 'dataMax']}
                />
                <YAxis
                  type="number"
                  dataKey="profit"
                  name="Profit Margin"
                  domain={['dataMin', 'dataMax']}
                />
                <Tooltip
                  formatter={(value, name) => [
                    name === 'price'
                      ? `$${value?.toLocaleString()}`
                      : `$${value?.toLocaleString()}`,
                    name === 'price' ? 'Asking Price' : 'Profit Margin',
                  ]}
                />
                <Scatter dataKey="profit" fill="#F59E0B" />
              </ScatterChart>
            </ResponsiveContainer>
          </div>
        );

      case 'profit':
        return (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Profit Margins by Make</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData.profitByMake}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="make" />
                  <YAxis />
                  <Tooltip
                    formatter={(value) => [`$${value?.toLocaleString()}`, 'Average Profit']}
                  />
                  <Bar dataKey="avgProfit" fill="#EF4444" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow p-6">
                <h4 className="text-md font-medium text-gray-900 mb-4">Top Performing Makes</h4>
                <div className="space-y-3">
                  {chartData.profitByMake
                    .sort((a, b) => b.avgProfit - a.avgProfit)
                    .slice(0, 5)
                    .map((make, index) => (
                      <div key={make.make} className="flex justify-between items-center">
                        <div className="flex items-center">
                          <div
                            className="w-3 h-3 rounded-full mr-3"
                            style={{ backgroundColor: COLORS[index % COLORS.length] }}
                          ></div>
                          <span className="text-sm font-medium">{make.make}</span>
                        </div>
                        <span className="text-sm text-gray-600">
                          ${make.avgProfit?.toLocaleString()}
                        </span>
                      </div>
                    ))}
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <h4 className="text-md font-medium text-gray-900 mb-4">Inventory Summary</h4>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Total Vehicles</span>
                    <span className="text-sm font-medium">{pricingData.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Average Price</span>
                    <span className="text-sm font-medium">
                      $
                      {(
                        pricingData.reduce((sum, v) => sum + (v.asking_price || 0), 0) /
                        pricingData.length
                      )?.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Average Profit</span>
                    <span className="text-sm font-medium">
                      $
                      {(
                        pricingData.reduce(
                          (sum, v) => sum + ((v.asking_price || 0) - (v.purchase_price || 0)),
                          0
                        ) / pricingData.length
                      )?.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'correlation':
        return (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Price vs Days on Lot</h3>
            <ResponsiveContainer width="100%" height={400}>
              <ScatterChart data={chartData.correlationData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  type="number"
                  dataKey="days"
                  name="Days on Lot"
                  domain={['dataMin', 'dataMax']}
                />
                <YAxis
                  type="number"
                  dataKey="price"
                  name="Asking Price"
                  domain={['dataMin', 'dataMax']}
                />
                <Tooltip
                  formatter={(value, name) => [
                    name === 'days' ? `${value} days` : `$${value?.toLocaleString()}`,
                    name === 'days' ? 'Days on Lot' : 'Asking Price',
                  ]}
                />
                <Scatter dataKey="price" fill="#8B5CF6" />
              </ScatterChart>
            </ResponsiveContainer>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Chart Type Selector */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-wrap gap-2 mb-6">
          <button
            onClick={() => setSelectedChart('trends')}
            className={`flex items-center px-4 py-2 rounded-md text-sm font-medium ${
              selectedChart === 'trends'
                ? 'bg-blue-100 text-blue-700 border-blue-300'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <ArrowTrendingUpIcon className="w-4 h-4 mr-2" />
            Price Trends
          </button>
          <button
            onClick={() => setSelectedChart('distribution')}
            className={`flex items-center px-4 py-2 rounded-md text-sm font-medium ${
              selectedChart === 'distribution'
                ? 'bg-blue-100 text-blue-700 border-blue-300'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <ChartBarIcon className="w-4 h-4 mr-2" />
            Price Distribution
          </button>
          <button
            onClick={() => setSelectedChart('profit')}
            className={`flex items-center px-4 py-2 rounded-md text-sm font-medium ${
              selectedChart === 'profit'
                ? 'bg-blue-100 text-blue-700 border-blue-300'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <CurrencyDollarIcon className="w-4 h-4 mr-2" />
            Profit Analysis
          </button>
          <button
            onClick={() => setSelectedChart('correlation')}
            className={`flex items-center px-4 py-2 rounded-md text-sm font-medium ${
              selectedChart === 'correlation'
                ? 'bg-blue-100 text-blue-700 border-blue-300'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <CalculatorIcon className="w-4 h-4 mr-2" />
            Correlations
          </button>
        </div>

        {renderChart()}
      </div>
    </div>
  );
};

export default PricingTrendCharts;
